<template>
  <div ref="containerRef" class="vci-container">
    <img
      ref="leftImageRef"
      :alt="leftImageAlt"
      :src="leftImage"
      :style="leftImageStyle"
      class="left-image"
    >

    <img ref="rightImageRef" :alt="rightImageAlt" :src="rightImage" class="right-image">

    <div :style="sliderStyle as any" class="vci-slider">
      <div :style="sliderLineStyle" class="line" />
      <div :style="sliderHandleStyle" class="handle">
        <div :style="sliderLeftArrowStyle" class="left-arrow" />
        <div :style="sliderRightArrowStyle" class="right-arrow" />
      </div>
      <div :style="sliderLineStyle" class="line" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useResizeObserver } from '@vueuse/core'
import { computed, onMounted, onUnmounted, ref } from 'vue'

interface IProps {
  leftImage: string
  rightImage: string
  leftImageAlt?: string
  rightImageAlt?: string
}

defineProps<IProps>()
const leftImageRef = ref<HTMLImageElement | null>(null)
const rightImageRef = ref<HTMLImageElement | null>(null)
const containerRef = ref<HTMLDivElement | null>(null)
const positionPct = ref(0.5)
const imageWidth = ref(0)
const hover = ref(false)
const handleSize = 40
const sliderLineWidth = 2

const leftImageStyle = computed(() => {
  return {
    clip: `rect(auto, ${imageWidth.value * positionPct.value}px, auto, auto)`,
  }
})

const sliderStyle = computed(() => {
  return {
    cursor: !hover.value && 'ew-resize',
    left: `${imageWidth.value * positionPct.value - handleSize / 2}px`,
    width: `${handleSize}px`,
  }
})

const sliderLineStyle = computed(() => {
  return { width: `${sliderLineWidth}px` }
})

const sliderHandleStyle = computed(() => {
  return {
    border: `${sliderLineWidth}px solid white`,
    height: `${handleSize}px`,
    width: `${handleSize}px`,
  }
})

const sliderLeftArrowStyle = computed(() => {
  return {
    border: `inset ${handleSize * 0.15}px rgba(0,0,0,0)`,
    borderRight: `${handleSize * 0.15}px solid white`,
    marginLeft: `-${handleSize * 0.25}px`, // for IE11
    marginRight: `${handleSize * 0.25}px`,
  }
})

const sliderRightArrowStyle = computed(() => {
  return {
    border: `inset ${handleSize * 0.15}px rgba(0,0,0,0)`,
    borderLeft: `${handleSize * 0.15}px solid white`,
    marginRight: `-${handleSize * 0.25}px`, // for IE11
  }
})

useResizeObserver(containerRef, () => {
  getAndSetImageWidth()
})

onMounted(() => {
  containerRef.value?.addEventListener('touchstart', startSliding)
  window.addEventListener('touchend', finishSliding)

  containerRef.value?.addEventListener('mousedown', startSliding)
  window.addEventListener('mouseup', finishSliding)
})

onUnmounted(() => {
  finishSliding()
  window.removeEventListener('mouseup', finishSliding)
  window.removeEventListener('touchend', finishSliding)
})

function getAndSetImageWidth() {
  imageWidth.value = rightImageRef.value!.getBoundingClientRect().width
}

function startSliding(e) {
  // Prevent default behavior other than mobile scrolling
  if (!('touches' in e)) {
    e.preventDefault()
  }

  // Slide the image even if you just click or tap (not drag)
  updateSliderPosition(e)

  window.addEventListener('mousemove', updateSliderPosition)
  window.addEventListener('touchmove', updateSliderPosition)
}

function finishSliding() {
  window.removeEventListener('mousemove', updateSliderPosition)
  window.removeEventListener('touchmove', updateSliderPosition)
}

function updateSliderPosition(event) {
  const e = event || window.event

  // Calc Cursor Position from the left edge of the viewport
  const cursorXfromViewport = e.touches ? e.touches[0].pageX : e.pageX

  // Calc Cursor Position from the left edge of the window (consider any page scrolling)
  const cursorXfromWindow = cursorXfromViewport - window.pageXOffset

  // Calc Cursor Position from the left edge of the image
  const imagePosition = rightImageRef.value?.getBoundingClientRect()
  let pos = cursorXfromWindow - imagePosition!.left

  const minPos = 0 + sliderLineWidth / 2
  const maxPos = imageWidth.value - sliderLineWidth / 2

  if (pos < minPos) { pos = minPos }
  if (pos > maxPos) { pos = maxPos }

  positionPct.value = pos / imageWidth.value
}
</script>

<style lang="scss" scoped>
.vci-container {
  box-sizing: border-box;
  position: relative;
  width: 100%;
  overflow: hidden;
  text-align: center;
  display: flex;
}

.left-image {
  display: block;
  height: 100%; // fit to the height of under image
  object-fit: contain; // protrudes is hidden
  position: absolute;
  top: 0;
  width: 100%;
}

.left-label {
  position: absolute;
  top: 0px;
  left: 0px;
  padding: 10px;
}

.right-image {
  display: block;
  height: 100%; // Respect the aspect ratio
  width: 100%;
  object-fit: contain; // protrudes is hidden
}

.right-label {
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 10px;
}

.vci-slider {
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  position: absolute;
  top: 0;

  .line {
    background: white;
    box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2),
      0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);
    flex: 0 1 auto;
    height: 100%;
  }

  .handle {
    align-items: center;
    border-radius: 100%;
    box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2),
      0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);
    box-sizing: border-box;
    display: flex;
    flex: 1 0 auto;
    justify-content: center;
  }

  .left-arrow {
    height: '0px';
    width: '0px';
  }

  .right-arrow {
    height: '0px';
    width: '0px';
  }
}
</style>
